import { ThemeProvider } from '@mui/material/styles'
import GlobalStyles from '@mui/material/GlobalStyles'
import CssBaseline from '@mui/material/CssBaseline'
import { appWithTranslation } from 'next-i18next'
import { TrackJS } from 'trackjs'
import { Lato } from 'next/font/google'
import dynamic from 'next/dynamic'
import type { AppProps } from 'next/app'
import { theme } from '@/theme/defaultTheme'
import Layout from '@/components/layout/Layout'
import 'suppress-experimental-warnings'
import { trackerConfig } from '@/logging/trackerJsConfig'
import { setupLogging } from '@/logging/clientLogger'
import { IntercomProvider } from '@/components/intercom/IntercomProvider'
import { AuthContextProvider } from '@/context/auth'
import '@/components/comments/onebox.scss'
import { isEmpty, isNil } from '@/helpers/helpers'

const LazySpriteMap = dynamic(() => import('@/assets/sprites/SpriteMap'), {
  ssr: false,
  suspense: true
})

const lato = Lato({
  variable: '--lato-font',
  weight: ['300', '400', '700'],
  style: ['normal', 'italic'],
  subsets: ['latin'],
  preload: true
})

if (typeof window !== 'undefined') {
  if (!TrackJS.isInstalled()) {
    TrackJS.install(trackerConfig)
  }
  setupLogging()
}

const App = ({ Component, pageProps }: AppProps): React.ReactElement => {
  // removing ref from pageProps to avoid React error (https://simscale.atlassian.net/browse/ENG-40097)
  // if a user added "ref" as query params it would break the app, because it would try to pass it to the component as the ref prop
  delete pageProps.ref

  const isBrowser = typeof window !== 'undefined'
  if (isBrowser) {
    // intercept and prevent wasteful /_next/data/*.json request until Next.js issue is resolved
    // https://github.com/vercel/next.js/discussions/38414
    // https://github.com/vercel/next.js/issues/40611
    const { fetch: originalFetch } = window
    const nextDataRequestRegex = /^\/_next\/data\/.*\.json/
    window.fetch = async (...args): Promise<Response> => {
      const [url] = args
      if (nextDataRequestRegex.test(url as string)) {
        return Promise.reject(new Error('Next.js data request blocked'))
      }
      return originalFetch(...args)
    }
  }

  // Workaround #4 for https://simscale.atlassian.net/browse/ENG-70447
  // This should be a temporary fix until we can find a better solution
  if (isNil(pageProps) || isEmpty(pageProps)) {
    if (isBrowser) {
      window.location.reload()
      // router.reload()
    }
    return <></>
  }

  return (
    <IntercomProvider>
      <AuthContextProvider>
        <ThemeProvider theme={theme}>
          <style jsx global>{`
            html,
            body {
              font-family: ${lato.style.fontFamily};
            }
          `}</style>
          <CssBaseline />
          <GlobalStyles styles={{}} />
          <Layout>
            <LazySpriteMap />
            <Component {...pageProps} />
          </Layout>
        </ThemeProvider>
      </AuthContextProvider>
    </IntercomProvider>
  )
}

export default appWithTranslation(App)
